/**
 * Integration Settings — timetoinvoice
 *
 * Admin-only card in the Settings tab that stores the timetoinvoice
 * address and API key server-side (sealed, write-only). The key field is
 * empty on every load, has no reveal toggle and no copy button, and is
 * cleared the moment a save succeeds — so a screenshot of this panel in
 * any normal state contains no key material.
 *
 * Hiding the form from non-admins is a courtesy; `requireConfigAdmin` on
 * the API is the real control, and a 403 is rendered, not assumed away.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { RefObject } from 'react';
import { useKeystoneSession } from '@/hooks/useKeystoneSession';
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings';
import type { UseIntegrationSettingsResult } from '@/hooks/useIntegrationSettings';
import type {
  IntegrationStatus,
  IntegrationTestResult,
  IntegrationTestStatus,
} from '@/api/integrationsApi';
import { formatDate, formatRelativeTime } from '@/utils/formatters';
import './IntegrationSettings.css';

const DEFAULT_TIMEOUT_MS = 5000;
const MIN_TIMEOUT_MS = 500;
const MAX_TIMEOUT_MS = 30000;
const MIN_KEY_LENGTH = 16;
const MAX_KEY_LENGTH = 512;
const CYCLE_START_DAY = 27;
const CYCLE_END_DAY = 26;

const NON_ADMIN_COPY = 'Integrations are managed by an administrator.';
const LOAD_FAILED_COPY = 'Could not load integration settings. Refresh to try again.';
const ERROR_STATE_COPY =
  'The saved connection could not be read, so the estimate is off. Enter the address and key again.';
const ENV_STATE_COPY =
  'A value set by the deployment is live. Saving here takes precedence over it — no redeploy needed.';
const NONE_STATE_COPY = 'Enter the timetoinvoice address and API key to turn the estimate on.';
const SAVE_ANYWAY_NOTE = 'The estimate will stay off until a test passes.';
const REMOVE_CONFIRM =
  'Remove the timetoinvoice key? The Revenue Target page will stop showing the billing estimate. You will need the key again to reconnect.';

const TEST_MESSAGES: Record<IntegrationTestStatus, string> = {
  ok: 'Connected.',
  no_snapshot: 'Connected, but timetoinvoice has no snapshot yet',
  unauthorised:
    'timetoinvoice rejected the key. Check you copied the whole key, or create a new one in timetoinvoice.',
  not_found:
    'Nothing answered at that address. Check it is the timetoinvoice site address, without /api on the end.',
  unreachable:
    'Could not reach timetoinvoice at that address. Check the address and that the site is online.',
  timeout: 'timetoinvoice took more than 5 seconds to answer. Try again in a minute.',
  bad_payload:
    'timetoinvoice answered in a format this app does not recognise. It may need an update.',
};

type PillTone = 'grey' | 'amber' | 'green';

interface FormState {
  baseUrl: string;
  timeoutMs: string;
  apiKey: string;
}

// ---------------------------------------------------------------------------
// Pure helpers

/** "2026-09" (a 27→26 cycle keyed by its end month) → "27 Aug – 26 Sep" */
function cycleLabel(cycleKey: string): string {
  const [year, month] = cycleKey.split('-').map(Number);
  const end = new Date(year, month - 1, CYCLE_END_DAY);
  const start = new Date(year, month - 2, CYCLE_START_DAY);
  return `${formatDate(start, 'd MMM')} – ${formatDate(end, 'd MMM')}`;
}

function testMessage(result: IntegrationTestResult): string {
  const copy = TEST_MESSAGES[result.status] ?? result.message;
  return result.ok && result.cycleKey ? `${copy} Open cycle ${cycleLabel(result.cycleKey)}.` : copy;
}

/** The five declared states, plus "checking" while a request is in flight */
function describeState(
  status: IntegrationStatus,
  busy: boolean
): { tone: PillTone; label: string; copy: string | null } {
  if (busy) return { tone: 'amber', label: 'Checking…', copy: null };
  if (status.status === 'error') {
    return { tone: 'amber', label: 'Needs attention', copy: ERROR_STATE_COPY };
  }
  if (status.source === 'none') {
    return { tone: 'grey', label: 'Not connected', copy: NONE_STATE_COPY };
  }
  if (status.source === 'env') {
    return { tone: 'green', label: 'Connected — set by deployment', copy: ENV_STATE_COPY };
  }
  if (status.lastTest && !status.lastTest.ok) {
    return { tone: 'amber', label: 'Saved — not verified', copy: null };
  }
  return { tone: 'green', label: 'Connected', copy: null };
}

function keyPlaceholder(status: IntegrationStatus): string {
  if (status.source === 'stored' && status.apiKey.present) {
    return 'Stored — leave blank to keep the current key';
  }
  if (status.source === 'env') return 'Set by deployment — enter a key to override';
  return 'Enter the API key';
}

/** Seed the editable fields from the server; the key is always empty. */
function seedFrom(status: IntegrationStatus): FormState {
  return {
    baseUrl: status.baseUrl ?? '',
    timeoutMs: String(status.timeoutMs || DEFAULT_TIMEOUT_MS),
    apiKey: '',
  };
}

// ---------------------------------------------------------------------------
// Form state and actions

/** Re-seed only when the persisted record changed, not on every refresh. */
function useSeededForm(status: IntegrationStatus) {
  const [form, setForm] = useState<FormState>(() => seedFrom(status));
  const seedKey = `${status.source}|${status.updatedAt}|${status.baseUrl}`;
  const lastSeedRef = useRef(seedKey);
  useEffect(() => {
    if (lastSeedRef.current === seedKey) return;
    lastSeedRef.current = seedKey;
    setForm(seedFrom(status));
  }, [seedKey, status]);
  return { form, setForm };
}

interface FormActions {
  settings: UseIntegrationSettingsResult;
  form: FormState;
  setForm: (next: FormState) => void;
  saved: boolean;
  keyInputRef: RefObject<HTMLInputElement>;
  submit: (skipTest: boolean) => Promise<void>;
  handleTest: () => void;
  handleReplaceKey: () => void;
  confirmingRemove: boolean;
  requestRemove: () => void;
  cancelRemove: () => void;
  confirmRemove: () => void;
}

function useFormActions(
  status: IntegrationStatus,
  settings: UseIntegrationSettingsResult
): FormActions {
  const { save, test, remove } = settings;
  const { form, setForm } = useSeededForm(status);
  const [saved, setSaved] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const keyInputRef = useRef<HTMLInputElement>(null);

  const submit = useCallback(
    async (skipTest: boolean) => {
      setSaved(false);
      const ok = await save({
        baseUrl: form.baseUrl.trim(),
        timeoutMs: Number(form.timeoutMs) || DEFAULT_TIMEOUT_MS,
        apiKey: form.apiKey === '' ? undefined : form.apiKey,
        skipTest: skipTest || undefined,
      });
      if (!ok) return;
      // Drop the key from the controlled input the moment it is stored.
      setForm({ ...form, apiKey: '' });
      setSaved(true);
    },
    [form, save, setForm]
  );
  const handleTest = useCallback(() => void test(), [test]);
  const handleReplaceKey = useCallback(() => {
    setForm({ ...form, apiKey: '' });
    keyInputRef.current?.focus();
  }, [form, setForm]);
  // Two-step, surface-native confirmation rather than window.confirm: a
  // native browser dialog gives this destructive, shared-artifact action
  // (removing the key everyone's estimate depends on) no more friction
  // than a plain alert, and it does not respect the busy/disabled guard,
  // so a rapid double-click could fire two removes before the first
  // response lands (UX-FLOW-01 / UX-DISC-03).
  const requestRemove = useCallback(() => setConfirmingRemove(true), []);
  const cancelRemove = useCallback(() => setConfirmingRemove(false), []);
  const confirmRemove = useCallback(() => {
    setConfirmingRemove(false);
    setSaved(false);
    void remove();
  }, [remove]);

  return {
    settings, form, setForm, saved, keyInputRef, submit, handleTest, handleReplaceKey,
    confirmingRemove, requestRemove, cancelRemove, confirmRemove,
  };
}

// ---------------------------------------------------------------------------
// Presentational pieces

function EvidenceLine({ status }: { status: IntegrationStatus }) {
  const last = status.lastTest;
  if (status.source !== 'stored' || !last) return null;
  if (last.status === 'no_snapshot') {
    return <p className="int-evidence">{TEST_MESSAGES.no_snapshot}</p>;
  }
  if (!last.ok) {
    return (
      <p className="int-evidence">
        Last test failed {formatRelativeTime(last.at)}. {SAVE_ANYWAY_NOTE}
      </p>
    );
  }
  const cycle = last.cycleKey ? ` · open cycle ${cycleLabel(last.cycleKey)}` : '';
  return (
    <p className="int-evidence">
      Checked {formatRelativeTime(last.at)}
      {cycle}
    </p>
  );
}

function FingerprintLine({ status }: { status: IntegrationStatus }) {
  const { apiKey, updatedAt, updatedBy, source } = status;
  if (!apiKey.present || !apiKey.fingerprint) return null;
  const provenance =
    source === 'env' || !updatedAt
      ? 'set by deployment'
      : `updated ${formatDate(updatedAt, 'd MMM yyyy')} by ${updatedBy ?? 'an administrator'}`;
  return (
    <span className="int-fingerprint">
      Key fingerprint <code>{apiKey.fingerprint}</code> · {provenance}
    </span>
  );
}

interface FieldProps {
  status: IntegrationStatus;
  actions: FormActions;
  busy: boolean;
}

function ConnectionFields({ actions, busy }: FieldProps) {
  const { form, setForm } = actions;
  return (
    <div className="rt-settings__fields">
      <label className="rt-field" htmlFor="int-base-url">
        <span className="rt-field__label">Base URL</span>
        <input
          id="int-base-url"
          type="url"
          value={form.baseUrl}
          onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
          disabled={busy}
          required
          className="rt-field__input"
          placeholder="https://timetoinvoice.example.com"
        />
        <span className="rt-field__hint">The site address only — no /api on the end</span>
      </label>
      <label className="rt-field" htmlFor="int-timeout">
        <span className="rt-field__label">Timeout (ms)</span>
        <input
          id="int-timeout"
          type="number"
          min={MIN_TIMEOUT_MS}
          max={MAX_TIMEOUT_MS}
          step="100"
          value={form.timeoutMs}
          onChange={(e) => setForm({ ...form, timeoutMs: e.target.value })}
          disabled={busy}
          className="rt-field__input"
        />
      </label>
    </div>
  );
}

/** Password input with no reveal toggle and no copy button — by design. */
function KeyField({ status, actions, busy }: FieldProps) {
  const { form, setForm, keyInputRef, handleReplaceKey } = actions;
  const keyStored = status.source === 'stored' && status.apiKey.present;
  return (
    <div className="rt-field rt-field--wide">
      <label className="rt-field__label" htmlFor="int-api-key">API key</label>
      <input
        id="int-api-key"
        ref={keyInputRef}
        type="password"
        autoComplete="off"
        spellCheck={false}
        minLength={MIN_KEY_LENGTH}
        maxLength={MAX_KEY_LENGTH}
        value={form.apiKey}
        onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
        disabled={busy}
        required={status.source === 'none' || status.status === 'error'}
        className="rt-field__input"
        placeholder={keyPlaceholder(status)}
      />
      <div className="rt-field__hint int-key-meta">
        <FingerprintLine status={status} />
        {keyStored && (
          <button
            type="button"
            className="int-btn int-btn--link"
            onClick={handleReplaceKey}
            disabled={busy}
          >
            Replace key
          </button>
        )}
      </div>
    </div>
  );
}

function FormMessages({ actions }: { actions: FormActions }) {
  const { saveError, testResult, saveBlockedByTest, saving } = actions.settings;
  const failure = testResult && !testResult.ok ? testMessage(testResult) : null;
  return (
    <div className="int-messages">
      {saveError && (
        <p className="int-message int-message--error" role="alert">
          {saveError}
        </p>
      )}
      {failure && (
        <div className="int-message int-message--error" role="alert">
          <p>{failure}</p>
          {saveBlockedByTest && (
            <p className="int-message__aside">
              <button
                type="button"
                className="int-btn int-btn--secondary"
                onClick={() => void actions.submit(true)}
                disabled={saving}
              >
                Save anyway
              </button>
              <span>{SAVE_ANYWAY_NOTE}</span>
            </p>
          )}
        </div>
      )}
      {testResult?.ok && (
        <p className="int-message int-message--ok" role="status">
          {testMessage(testResult)}
        </p>
      )}
      {actions.saved && !saveError && !saveBlockedByTest && (
        <p className="int-message int-message--ok" role="status">Saved</p>
      )}
    </div>
  );
}

function RemoveConfirm({ actions, busy }: FieldProps) {
  return (
    <div className="int-remove-confirm" role="alertdialog" aria-label="Confirm remove">
      <p>{REMOVE_CONFIRM}</p>
      <div className="int-remove-confirm__actions">
        <button
          type="button"
          className="int-btn int-btn--secondary"
          onClick={actions.cancelRemove}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="int-btn int-btn--danger"
          onClick={actions.confirmRemove}
          disabled={busy}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function FormButtons({ status, actions, busy }: FieldProps) {
  const keyStored = status.source === 'stored' && status.apiKey.present;

  if (actions.confirmingRemove) {
    return <RemoveConfirm status={status} actions={actions} busy={busy} />;
  }

  return (
    <div className="rt-settings__actions int-actions">
      <button type="submit" disabled={busy} className="rt-settings__save">
        {keyStored ? 'Save' : 'Connect and test'}
      </button>
      {status.source !== 'none' && (
        <button
          type="button"
          className="int-btn int-btn--secondary"
          onClick={actions.handleTest}
          disabled={busy}
        >
          Test connection
        </button>
      )}
      {status.source === 'stored' && (
        <button
          type="button"
          className="int-btn int-btn--danger"
          onClick={actions.requestRemove}
          disabled={busy}
        >
          Remove
        </button>
      )}
    </div>
  );
}

function IntegrationForm({
  status,
  settings,
}: {
  status: IntegrationStatus;
  settings: UseIntegrationSettingsResult;
}) {
  const actions = useFormActions(status, settings);
  const busy = settings.saving || settings.testing || settings.removing;
  return (
    <form
      className="rt-settings__form int-form"
      onSubmit={(e) => {
        e.preventDefault();
        void actions.submit(false);
      }}
    >
      <ConnectionFields status={status} actions={actions} busy={busy} />
      <KeyField status={status} actions={actions} busy={busy} />
      <FormMessages actions={actions} />
      <FormButtons status={status} actions={actions} busy={busy} />
    </form>
  );
}

// ---------------------------------------------------------------------------
// Card

/** Mounted only for admins so non-admins never issue the status GET. */
function AdminCardBody() {
  const settings = useIntegrationSettings();
  const { status, loading, loadError, saving, testing, removing } = settings;
  if (loading && !status) {
    return <p className="int-state" role="status">Loading integration settings…</p>;
  }
  // Fail closed: no readable status means no form.
  if (loadError || !status) {
    return (
      <p className="int-state int-state--error" role="alert">
        {loadError ?? LOAD_FAILED_COPY}
      </p>
    );
  }
  const state = describeState(status, saving || testing || removing);
  return (
    <>
      <span className={`int-pill int-pill--${state.tone}`} role="status">
        {state.label}
      </span>
      {state.copy && <p className="int-state">{state.copy}</p>}
      <EvidenceLine status={status} />
      <IntegrationForm key={status.source} status={status} settings={settings} />
    </>
  );
}

export function IntegrationSettings() {
  const { user } = useKeystoneSession();
  const isAdmin = user?.appRole === 'admin';
  return (
    <section className="rt-section rt-settings int-card" aria-labelledby="int-card-title">
      <h3 id="int-card-title" className="int-card__title">
        Billing estimate — timetoinvoice
      </h3>
      <p className="int-card__purpose">
        Shows the value of unbilled work on the Revenue Target page.
      </p>
      {isAdmin ? <AdminCardBody /> : <p className="int-state">{NON_ADMIN_COPY}</p>}
    </section>
  );
}
